import { api } from "@convex/_generated/api";
import { type Control, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { convexClient } from "~/integrations/convex/provider";

import { Button } from "../ui/button";

// Avoids TS2589 (type instantiation is excessively deep) from api.d.ts chain
const checkIfPinInUseRef = api.employees.checkIfPinInUse;

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";

// Top-level regex for PIN validation (4 digits)
const PIN_PATTERN = /^\d{4}$/;

interface PinFormData {
  pin: string;
  confirmPin: string;
}

export const PinForm = ({
  onSubmit,
  submitButtonText,
  className,
  value,
  checkIfPinInUse,
}: {
  onSubmit: (pin: string) => void;
  submitButtonText: string;
  className?: string;
  value?: string;
  checkIfPinInUse?: boolean;
}) => {
  const form = useForm<PinFormData>({
    defaultValues: {
      confirmPin: value ?? "",
      pin: value ?? "",
    },
  });

  return (
    <Form {...form}>
      <form
        className={twMerge("flex flex-col items-center gap-6", className)}
        onSubmit={form.handleSubmit(async (data) => {
          if (data.pin !== data.confirmPin) {
            form.setError("confirmPin", {
              message: "PINs do not match",
            });
            return;
          }
          if (checkIfPinInUse) {
            const isPinInUse = await convexClient.query(checkIfPinInUseRef, {
              pin: data.pin,
            });
            if (isPinInUse) {
              form.setError("root", {
                message: "PIN already in use. Please choose a different PIN.",
              });
              form.setError("pin", {
                message: "",
              });
              return;
            }
          }
          onSubmit(data.pin);
        })}
      >
        <div className="max-w-30 space-y-5">
          <PinField control={form.control} label="4 digit PIN" name="pin" />
          <PinField
            control={form.control}
            label="Confirm PIN"
            name="confirmPin"
          />
        </div>
        <FormMessage>{form.formState.errors.root?.message}</FormMessage>
        <Button
          className="w-full"
          disabled={
            form.watch("pin") !== form.watch("confirmPin") ||
            form.watch("pin").length !== 4 ||
            !PIN_PATTERN.test(form.watch("pin"))
          }
          size="lg"
          type="submit"
        >
          {submitButtonText}
        </Button>
      </form>
    </Form>
  );
};

const PinField = ({
  name,
  control,
  label,
}: {
  name: "pin" | "confirmPin";
  control: Control<PinFormData>;
  label: string;
}) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FormLabel className="mx-auto">{label}</FormLabel>
        <FormControl>
          <InputOTP
            maxLength={4}
            onChange={field.onChange}
            pattern="[0-9]*"
            value={field.value}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
            </InputOTPGroup>
          </InputOTP>
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);
